import { DateTime } from 'luxon';
import {
  type ITask,
  TASK_AUTO_TYPE_LABELS,
  TASK_RECURRENCE_LABELS,
  TaskSourceEnum,
  TaskStatusEnum,
} from '@torim-app/types';

const ZONE = 'Asia/Jerusalem';

export const todayIso = (): string => DateTime.local({ zone: ZONE }).toISODate() as string;
export const isoPlusDays = (days: number, from = todayIso()): string =>
  DateTime.fromISO(from, { zone: ZONE }).plus({ days }).toISODate() as string;

export const formatHour = (hour?: number | null): string => {
  if (hour == null) return '';
  const h = Math.floor(hour);
  const m = Math.round((hour - h) * 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

export const parseHour = (hhmm: string): number | null => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(hhmm ?? '');
  if (!match) return null;
  return Number(match[1]) + Number(match[2]) / 60;
};

export const isDone = (task: ITask) => task.status === TaskStatusEnum.Done;
export const isOverdue = (task: ITask) => !isDone(task) && !!task.dueDate && task.dueDate < todayIso();

/** "היום", "מחר", "אתמול", a weekday this week, else "5.11". */
export const dueDayLabel = (iso?: string | null): string => {
  if (!iso) return 'בלי תאריך';
  const today = todayIso();
  if (iso === today) return 'היום';
  if (iso === isoPlusDays(1)) return 'מחר';
  if (iso === isoPlusDays(-1)) return 'אתמול';
  const date = DateTime.fromISO(iso, { zone: ZONE }).setLocale('he');
  if (iso > today && iso <= isoPlusDays(6)) return date.toFormat('cccc');
  return date.toFormat('d.M');
};

/** One-line "when" for a task row: due day/time, or the reserved range. */
export const dueLabel = (task: ITask): string => {
  const day = dueDayLabel(task.dueDate);
  if (!task.dueDate) return day;
  if (task.reservation) return `${day} · ${formatHour(task.reservation.from)}–${formatHour(task.reservation.to)} · משוריין ביומן`;
  return task.dueTime != null ? `${day} · ${formatHour(task.dueTime)}` : day;
};

export const sourceChip = (task: ITask): string | null => {
  if (task.source !== TaskSourceEnum.Auto || !task.autoType) return null;
  return `אוטומטית · ${TASK_AUTO_TYPE_LABELS[task.autoType]}`;
};

export const recurrenceLabel = (task: ITask): string | null =>
  task.recurrence ? TASK_RECURRENCE_LABELS[task.recurrence] : null;

export type TaskFilter = 'all' | 'auto' | 'customers' | 'recurring';

export const matchesFilter = (task: ITask, filter: TaskFilter): boolean => {
  if (filter === 'auto') return task.source === TaskSourceEnum.Auto;
  if (filter === 'customers') return !!task.customerId;
  if (filter === 'recurring') return !!task.recurrence;
  return true;
};

export interface ITaskGroup {
  key: string;
  label: string;
  danger?: boolean;
  tasks: ITask[];
}

/** Open tasks by when they're due: overdue, today, tomorrow, this week, later, no date. */
export const groupOpenTasks = (tasks: ITask[]): ITaskGroup[] => {
  const today = todayIso();
  const tomorrow = isoPlusDays(1);
  const weekEnd = isoPlusDays(6);
  const groups: ITaskGroup[] = [
    { key: 'overdue', label: 'באיחור', danger: true, tasks: [] },
    { key: 'today', label: 'היום', tasks: [] },
    { key: 'tomorrow', label: 'מחר', tasks: [] },
    { key: 'week', label: 'השבוע', tasks: [] },
    { key: 'later', label: 'בהמשך', tasks: [] },
    { key: 'none', label: 'בלי תאריך', tasks: [] },
  ];
  const at = (key: string) => groups.find(g => g.key === key)!.tasks;
  for (const task of tasks) {
    const due = task.dueDate;
    if (!due) at('none').push(task);
    else if (due < today) at('overdue').push(task);
    else if (due === today) at('today').push(task);
    else if (due === tomorrow) at('tomorrow').push(task);
    else if (due <= weekEnd) at('week').push(task);
    else at('later').push(task);
  }
  return groups.filter(g => g.tasks.length);
};

/** Reminder presets, resolved against the task's due day/time. */
export type ReminderPreset = 'none' | 'at-time' | 'morning' | 'day-before';

export const REMINDER_OPTIONS: Array<{ value: ReminderPreset; label: string }> = [
  { value: 'none', label: 'ללא' },
  { value: 'at-time', label: 'בשעת המשימה' },
  { value: 'morning', label: 'ב-09:00 ביום' },
  { value: 'day-before', label: 'יום לפני, 09:00' },
];

export const reminderAt = (preset: ReminderPreset, dueDate?: string | null, dueTime?: number | null): string | null => {
  if (preset === 'none' || !dueDate) return null;
  const day = DateTime.fromISO(dueDate, { zone: ZONE });
  if (preset === 'at-time') {
    if (dueTime == null) return day.set({ hour: 9 }).toUTC().toISO();
    return day.plus({ minutes: Math.round(dueTime * 60) }).toUTC().toISO();
  }
  if (preset === 'morning') return day.set({ hour: 9 }).toUTC().toISO();
  return day.minus({ days: 1 }).set({ hour: 9 }).toUTC().toISO();
};

/** Best guess of which preset a stored reminder came from (for editing). */
export const reminderPresetOf = (task: Pick<ITask, 'reminderAt' | 'dueDate' | 'dueTime'>): ReminderPreset => {
  if (!task.reminderAt) return 'none';
  const at = new Date(task.reminderAt).getTime();
  for (const option of ['at-time', 'morning', 'day-before'] as ReminderPreset[]) {
    const iso = reminderAt(option, task.dueDate, task.dueTime);
    if (iso && new Date(iso).getTime() === at) return option;
  }
  return 'morning';
};
